import clsx from 'clsx';
import React from 'react';

import Button from '../../../../../../../../common/_custom/Button/Button';
import { KEYBOARD_EVENTS } from '../../../../../../../../utils/constants/commonConstants';
import {
    CLARIFICATION_COPY,
    CLARIFICATION_TYPED_ANSWER_MAX_LENGTH,
    TYPE_ANSWER_LABEL,
} from '../../../../../../../../store/dashboard/askAica/constants';
import {
    ClarificationAnswer,
    ClarificationCard as ClarificationCardData,
} from '../../../../../../../../store/dashboard/askAica/clarificationTypes';
import { useClarificationCardLogic } from './useClarificationCardLogic';

import styles from './ClarificationCard.module.scss';

interface ClarificationCardProps {
    card: ClarificationCardData;
    onSubmit: (answers: ClarificationAnswer[]) => void;
    disabled?: boolean;
}

/**
 * The docked, interactive card for a round with questions pending — one question at a time,
 * rendered above the composer (see AskAicaInputBox). Every string here is model-authored, so
 * it is only ever rendered as a React text node: never markdown, never innerHTML.
 */
const ClarificationCard: React.FC<ClarificationCardProps> = ({ card, onSubmit, disabled = false }) => {
    const {
        question,
        index,
        total,
        pickedOptionId,
        typedText,
        isTyping,
        draft,
        inputRef,
        goTo,
        selectOption,
        startTyping,
        updateDraft,
        submitTyped,
    } = useClarificationCardLogic({ card, onSubmit });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== KEYBOARD_EVENTS.ENTER) return;
        event.preventDefault();
        submitTyped();
    };

    return (
        <div className={styles.ClarificationCard} data-testid="clarification-card">
            <div className={styles.Header}>
                <span className={styles.Question}>{question.question}</span>
                {total > 1 && (
                    <div className={styles.Pager}>
                        <Button
                            type="button"
                            className={styles.PagerButton}
                            onClick={() => goTo(index - 1)}
                            disabled={index === 0}
                            aria-label={CLARIFICATION_COPY.PREVIOUS_QUESTION}
                            text="‹"
                        />
                        <span className={styles.PagerLabel} data-testid="clarification-pager">
                            {index + 1} of {total}
                        </span>
                        <Button
                            type="button"
                            className={styles.PagerButton}
                            onClick={() => goTo(index + 1)}
                            disabled={index === total - 1}
                            aria-label={CLARIFICATION_COPY.NEXT_QUESTION}
                            text="›"
                        />
                    </div>
                )}
            </div>

            <div className={styles.Options} role="radiogroup" aria-label={question.header}>
                {question.options.map((option) => {
                    const isSelected = pickedOptionId === option.id;
                    return (
                        <button
                            key={option.id}
                            type="button"
                            role="radio"
                            aria-checked={isSelected}
                            className={clsx(styles.CandidateRow, { [styles.Selected]: isSelected })}
                            onClick={() => selectOption(option.id)}
                            disabled={disabled}
                            title={option.description}
                            data-testid="clarification-option"
                        >
                            <span className={styles.RadioDot} aria-hidden="true" />
                            <span className={styles.OptionLabel}>{option.label}</span>
                            {option.recommended && <span className={styles.Recommended}>{CLARIFICATION_COPY.RECOMMENDED}</span>}
                        </button>
                    );
                })}

                {isTyping ? (
                    <div className={clsx(styles.CandidateRow, styles.TypeRowActive)} data-testid="clarification-type-row">
                        <span className={styles.RadioDot} aria-hidden="true" />
                        <input
                            ref={inputRef}
                            className={styles.TypeInput}
                            value={draft}
                            maxLength={CLARIFICATION_TYPED_ANSWER_MAX_LENGTH}
                            onChange={(event) => updateDraft(event.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={TYPE_ANSWER_LABEL}
                            disabled={disabled}
                            aria-label={TYPE_ANSWER_LABEL}
                            data-testid="clarification-type-input"
                        />
                        <Button
                            type="button"
                            className={styles.EnterChip}
                            onClick={submitTyped}
                            disabled={disabled}
                            text={`↵ ${CLARIFICATION_COPY.ENTER}`}
                        />
                    </div>
                ) : (
                    <button
                        type="button"
                        role="radio"
                        aria-checked={typedText !== undefined}
                        className={clsx(styles.CandidateRow, styles.TypeRow, { [styles.Selected]: typedText !== undefined })}
                        onClick={startTyping}
                        disabled={disabled}
                        data-testid="clarification-type-answer"
                    >
                        <span className={styles.RadioDot} aria-hidden="true" />
                        <span className={styles.OptionLabel}>{typedText ?? TYPE_ANSWER_LABEL}</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default ClarificationCard;
